// src/páginas/inventario/InventoryPage.jsx
import React, { useState, useEffect } from 'react';
import { Home, Filter, Download, Search } from 'lucide-react';
import { getInventoryStats, getAvailableUnits } from '../../services/inventory';
import MainLayout from '../../components/layout/MainLayout';

const InventoryPage = () => {
  const [stats, setStats] = useState(null);
  const [units, setUnits] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    floor: 'all'
  });
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const loadInventoryData = async () => {
      setIsLoading(true);
      try {
        const [statsData, unitsData] = await Promise.all([
          getInventoryStats(),
          getAvailableUnits(filters)
        ]);
        
        setStats(statsData);
        setUnits(unitsData);
      } catch (error) {
        console.error('Erro ao carregar dados de inventário:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInventoryData();
  }, [filters]);
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
            Disponível
          </span>
        );
      case 'reserved':
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Reservado
          </span>
        );
      case 'sold':
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
            Vendido
          </span>
        );
      case 'blocked':
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            Bloqueado
          </span>
        );
      default:
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };
  
  return (
    <MainLayout>
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900">Inventário</h1>
        <p className="text-gray-500">Visualize e gerencie o estoque de unidades</p>
      </div>
      
      {/* Status Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between">
              <div>
                <div className="text-sm text-gray-500 mb-1">Disponíveis</div>
                <div className="text-2xl font-bold text-green-600">{stats.available}</div>
              </div>
              <div className="p-2 bg-green-100 rounded-md text-green-600">
                <Home className="w-6 h-6" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between">
              <div>
                <div className="text-sm text-gray-500 mb-1">Reservadas</div>
                <div className="text-2xl font-bold text-yellow-600">{stats.reserved}</div>
              </div>
              <div className="p-2 bg-yellow-100 rounded-md text-yellow-600">
                <Home className="w-6 h-6" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between">
              <div>
                <div className="text-sm text-gray-500 mb-1">Vendidas</div>
                <div className="text-2xl font-bold text-red-600">{stats.sold}</div>
              </div>
              <div className="p-2 bg-red-100 rounded-md text-red-600">
                <Home className="w-6 h-6" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between">
              <div>
                <div className="text-sm text-gray-500 mb-1">Total</div>
                <div className="text-2xl font-bold text-blue-900">{stats.total}</div>
              </div>
              <div className="p-2 bg-blue-100 rounded-md text-blue-900">
                <Home className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Filters and Table */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar unidade..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
            </div>
          </div>
          
          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">Todos os status</option>
              <option value="available">Disponível</option>
              <option value="reserved">Reservado</option>
              <option value="sold">Vendido</option>
              <option value="blocked">Bloqueado</option>
            </select>
          </div>
          
          <div>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">Todos os tipos</option>
              <option value="2 quartos">2 Quartos</option>
              <option value="3 quartos">3 Quartos</option>
            </select>
          </div>
          
          <div>
            <select
              value={filters.floor}
              onChange={(e) => handleFilterChange('floor', e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">Todos os andares</option>
              {[4, 5, 6, 7, 8, 9, 10].map(floor => (
                <option key={floor} value={floor}>Andar {floor}</option>
              ))}
            </select>
          </div>
          
          <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md flex items-center">
            <Filter className="w-4 h-4 mr-1" /> Mais filtros
          </button>
          
          <button className="px-3 py-2 bg-blue-900 text-white rounded-md flex items-center">
            <Download className="w-4 h-4 mr-1" /> Exportar
          </button>
        </div>
        
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unidade
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Área
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Andar
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preço
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded mb-2.5"></div>
                      <div className="h-4 bg-gray-200 rounded mb-2.5"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                  </td>
                </tr>
              ) : units.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    Nenhuma unidade encontrada com os filtros atuais
                  </td>
                </tr>
              ) : (
                units.map(unit => (
                  <tr key={unit.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{unit.number}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-500">{unit.type}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-500">{unit.area}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-500">{unit.floor}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(unit.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-900 font-medium">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(unit.price)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button className="text-blue-900 hover:text-blue-700 mr-3">
                        Ver
                      </button>
                      <button className="text-blue-900 hover:text-blue-700">
                        Editar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </MainLayout>
  );
};

export default InventoryPage;